import { useCallback, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import ReactGA from "react-ga4";
import { Dialog } from "@material-ui/core";
import "./campaign.scss";

import Title from "../../../uiKit/Title/Title";
import CustomInput from "../../../uiKit/Input/CustomInput";
import CustomButton from "../../../uiKit/Button/CustomButton";
import { MailMan } from "../../../uiKit/assets/svg/mailMan";
import CustomDialog, { useStylesDialog } from "../../../uiKit/CustomDialog/customDialog";
import { onlyNumbers } from "../../../helpers/validation";

import {
  getCampaignBalanceDetails,
  transferFromOurBalance,
} from "../../../redux/state/paymentSlice";
import { setTransferBalance } from "../../../redux/state/donationSlice";
import { getCampaignDonations } from "../../../redux/state/campaignSlice";
import { getCommunity } from "../../../redux/state/communitySlice";

const Transfer = () => {
  const dispatch = useDispatch();
  const dialogClasses = useStylesDialog();

  const { campaignId } = useParams();

  const [amount, setAmount] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState("");

  const transferBalance = useSelector((state) => state.donation.transferBalance, shallowEqual);
  const communityId = useSelector((state) => state.community.community.id, shallowEqual);
  const campaignName = useSelector((state) => state.campaign.campaign.name, shallowEqual);
  const target = useSelector((state) => state.campaign.campaign?.amount, shallowEqual);
  const communityBalance = useSelector(
    (state) => state.community.community.ourBalance,
    shallowEqual
  );
  const isLoading = useSelector(
    (state) => state.payment.isLoading.transferFromOurBalance,
    shallowEqual
  );

  const openTransferFromOurBalanceHandler = useCallback(() => {
    ReactGA.event("transfer_from_our_balance");
    dispatch(setTransferBalance(true));
  }, [dispatch]);

  const closeTransferFromOurBalanceHandler = useCallback(
    () => dispatch(setTransferBalance(false)),
    [dispatch]
  );

  const confirmHandler = () => {
    if (!amount) {
      setError("Invalid amount");
    } else if (amount > communityBalance) {
      setError("Not enough money on Our Balance");
    } else {
      setConfirmOpen(true);
    }
  };

  const onChangeHandler = (e) => {
    const value = e.target.value;
    if (onlyNumbers(value)) {
      setAmount(e.target.value);
      setError("");
    } else {
      setError("Only numbers allowed");
    }
  };

  const transferToCampaign = useCallback(() => {
    if (!communityId || !campaignId || !target) return;
    const data = {
      outgoingCommunityId: communityId,
      incomingCamapaignId: campaignId,
      amount: parseInt(amount),
    };
    dispatch(transferFromOurBalance({ communityId, campaignId, data }))
      .then(unwrapResult)
      .then(() => {
        setTimeout(() => {
          if (!communityId || !campaignId || !target) return;
          dispatch(getCampaignBalanceDetails({ communityId, campaignId, target }));
          dispatch(getCommunity(communityId));
          dispatch(
            getCampaignDonations({
              communityId,
              campaignId,
              page: 1,
              pageSize: 8,
            })
          );
        }, 2000);
        setConfirmOpen(false);
        setAmount(0);
        closeTransferFromOurBalanceHandler();
      })
      .catch(({ title }) => {
        toast.info(`Transfer failed: ${title}`);
      });
  }, [communityId, campaignId, amount, target, closeTransferFromOurBalanceHandler, dispatch]);

  return (
    <>
      <CustomButton
        title="Transfer from Our Balance"
        isWhite
        classNames="margin16to0"
        onClick={openTransferFromOurBalanceHandler}
      />

      <Dialog
        open={transferBalance}
        onClose={closeTransferFromOurBalanceHandler}
        disableScrollLock={true}
        classes={dialogClasses}
      >
        <div className="page">
          <div className="feedback feedbackContainer">
            <div className="feedbackImageContainer">
              <MailMan />
            </div>
          </div>
          <Title
            text="Transfer amount:"
            fontSize={32}
            lineHeight={40}
            style={{
              marginBottom: "16px",
            }}
          />
          <CustomInput
            description={`Total on Our Balance $${communityBalance.toLocaleString()}`}
            onChange={onChangeHandler}
            value={!!amount ? amount : ""}
            style={{
              marginBottom: "80px",
            }}
            error={!!error}
            errorMessage={error}
            inputMode="numeric"
          />
          <CustomButton
            title="Transfer from Bank"
            onClick={confirmHandler}
            disabled={!communityBalance}
          />
        </div>
      </Dialog>

      <CustomDialog
        confirmOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        Image={MailMan}
        mainButtonTitle="Confirm"
        mainButtonAction={transferToCampaign}
        isLoading={isLoading}
        secondButton={true}
        title={`You're going transfer $${parseInt(
          amount
        ).toLocaleString()} to ${campaignName} from our balance.`}
        secondDescription="Confirm your action, please"
      />
    </>
  );
};

export default Transfer;
